import React, { useState } from 'react';
import styled, { css } from 'styled-components';
import { useRouter } from 'next/router';
import { omit } from 'lodash';

import { useMedia } from '@tager/web-core';

import { media } from '@/utils/mixin';
import { getFilterParamAsStringArray } from '@/utils/common';
import { useTypedDispatch } from '@/store/store';
import { fetchPostsListPostsThunk } from '@/store/reducers/posts-list';
import { colors } from '@/constants/theme';

import Tag from './components/Tag';
import { FilterByTagsProps, FilterByTagsTagType } from './types';

function FiltersByTags({ tags, category, resultText }: FilterByTagsProps) {
  const dispatch = useTypedDispatch();
  const router = useRouter();
  const [selectTags, setSelectTags] = useState<FilterByTagsTagType[]>(
    (tags ?? []).filter((tag) =>
      getFilterParamAsStringArray(router.query, 'tag').some(
        (urlAlias) => urlAlias === tag.urlAlias
      )
    )
  );
  const isTablet = useMedia('(max-width: 1259px)');
  const isSelectedTags = selectTags.length > 0;

  const isSelected = (tag: FilterByTagsTagType) => {
    return selectTags.some(
      (selectedTag) => selectedTag.urlAlias === tag.urlAlias
    );
  };

  const handleTagClick = async (tag: FilterByTagsTagType) => {
    const newSelectedTags = isSelected(tag)
      ? selectTags.filter((selectTag) => selectTag.urlAlias !== tag.urlAlias)
      : [...selectTags, tag];

    setSelectTags(newSelectedTags);

    await Promise.all([
      router.replace(
        {
          query: {
            ...omit(router.query, 'tag'),
            tag: newSelectedTags.map((tag) => tag.urlAlias ?? ''),
          },
        },
        undefined,
        { shallow: true }
      ),
      dispatch(
        fetchPostsListPostsThunk({
          category,
          tags: newSelectedTags.map(({ urlAlias }) => urlAlias ?? ''),
        })
      ),
    ]);
  };

  const handleTagsClear = async () => {
    setSelectTags([]);
    await router.replace('');
  };

  return (
    <Component>
      <Header>
        <Title>Фильтр по тегам</Title>
        <Clear onClick={handleTagsClear} isSelectedTags={isSelectedTags}>
          Очистить
        </Clear>
      </Header>

      <Result isSelectedTags={isSelectedTags}>
        <ResultText>{resultText}</ResultText>
        {isTablet && (
          <Clear onClick={handleTagsClear} isSelectedTags={isSelectedTags}>
            Очистить
          </Clear>
        )}
      </Result>

      <TagsList>
        {tags?.map((tag, index) => (
          <Tag
            key={index}
            tag={tag.tag}
            onClick={() => handleTagClick(tag)}
            isSelected={isSelected(tag)}
          />
        ))}
      </TagsList>
    </Component>
  );
}

export default FiltersByTags;

const Component = styled.div`
  display: flex;
  flex-direction: column;

  ${media.tablet(css`
    margin-top: 10px;
  `)}
`;

const Header = styled.div`
  display: flex;
  align-items: flex-end;

  ${media.tablet(css`
    display: none;
  `)}
`;

const Title = styled.span`
  font-weight: 700;
  font-size: 24px;
  line-height: 32px;
  color: ${(props) => props.theme.black};

  ${media.tablet(css`
    display: none;
  `)}
`;

const Clear = styled.span<{ isSelectedTags: boolean }>`
  display: none;
  cursor: pointer;
  font-size: 16px;
  line-height: 24px;
  margin-left: 18px;
  color: ${colors.main};
  transition: 0.3s;

  ${media.tablet(css`
    font-size: 14px;
    line-height: 20px;
    margin-left: 10px;
  `)}

  &:hover {
    opacity: 0.8;
  }

  ${({ isSelectedTags }) =>
    isSelectedTags &&
    css`
      display: block;
    `}
`;

const Result = styled.div<{ isSelectedTags: boolean }>`
  display: none;
  margin-top: 10px;

  ${({ isSelectedTags }) =>
    isSelectedTags &&
    css`
      display: flex;
    `}
`;

const ResultText = styled.span`
  font-size: 14px;
  line-height: 20px;
  color: ${colors.gray};
`;

const TagsList = styled.div`
  display: flex;
  flex-wrap: wrap;
  margin-top: 30px;

  ${media.tablet(css`
    margin-top: 10px;
  `)}
`;
